import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import BrandNavbar from '../components/dashboard/BrandNavbar';
import PublicCreatorProfile from '../components/PublicCreatorProfile';

const LiveBidsPage = () => {
  const { user } = useAuth();
  const [campaignsWithBids, setCampaignsWithBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('error'); // 'error' or 'success'
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showBidsModal, setShowBidsModal] = useState(false);
  const [selectedCreatorId, setSelectedCreatorId] = useState(null);
  const [showCreatorProfile, setShowCreatorProfile] = useState(false);
  const [processingBidId, setProcessingBidId] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'brand') return;
    
    const loadLiveBids = async () => {
      try {
        const response = await api.get(`/brands/${user.userId}/live-bids`);
        setCampaignsWithBids(response.data.campaigns || []);
      } catch (error) {
        setMessage('Failed to load live bids');
        setMessageType('error');
      } finally {
        setLoading(false);
      }
    };

    loadLiveBids();
  }, [user]);

  const openBidsModal = (campaign) => {
    setSelectedCampaign(campaign);
    setShowBidsModal(true);
  };

  const closeBidsModal = () => {
    setShowBidsModal(false);
    setSelectedCampaign(null);
  };

  const openCreatorProfile = (creatorId) => {
    setSelectedCreatorId(creatorId);
    setShowCreatorProfile(true);
  };

  const closeCreatorProfile = () => {
    setShowCreatorProfile(false);
    setSelectedCreatorId(null);
  };

  const acceptBid = async (campaignId, bidId) => {
    try {
      setProcessingBidId(bidId);
      
      const response = await api.post(`/brands/${user.userId}/campaigns/${campaignId}/bids/${bidId}/accept`);
      
      if (response.data) {
        setMessage('Bid accepted successfully!');
        setMessageType('success');
        
        // Refresh the bids data
        const updatedResponse = await api.get(`/brands/${user.userId}/live-bids`);
        setCampaignsWithBids(updatedResponse.data.campaigns || []);
        
        // Update the selected campaign if modal is open
        if (selectedCampaign && selectedCampaign.campaignId === campaignId) {
          const updatedCampaign = updatedResponse.data.campaigns.find(c => c.campaignId === campaignId);
          if (updatedCampaign) {
            setSelectedCampaign(updatedCampaign);
          }
        }
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to accept bid. Please try again.');
      setMessageType('error');
    } finally {
      setProcessingBidId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getBidStatusColor = (status) => {
    switch (status) {
      case 'ACCEPTED': return 'text-green-600 bg-green-100';
      case 'REJECTED': return 'text-red-600 bg-red-100';
      case 'PENDING': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (!user || user.role !== 'brand') {
    return <div className="p-8 text-center">Access denied. Only brands can view this page.</div>;
  }

  if (loading) {
    return (
      <>
        <BrandNavbar />
        <div className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading live bids...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Live Bids - Brand Dashboard | CollabOrb</title>
        <meta name="description" content="View and manage live bids on your active campaigns" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <BrandNavbar />

      <div className="min-h-screen bg-gray-50 py-8 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Live Bids</h1>
              <p className="mt-2 text-gray-600">Monitor and manage bids on your active campaigns</p>
            </div>
          </div>

          {/* Message Display */}
          {message && (
            <div className={`mb-6 p-4 rounded-md border ${
              messageType === 'success' 
                ? 'bg-green-50 text-green-700 border-green-200' 
                : 'bg-red-50 text-red-700 border-red-200'
            }`}>
              {message}
              <button 
                onClick={() => setMessage('')}
                className="ml-2 text-sm underline hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Campaigns with Bids */}
          {campaignsWithBids.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Live Bids</h3>
              <p className="text-gray-600">You don't have any active campaigns with bids at the moment.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {campaignsWithBids.map((campaign) => (
                <div key={campaign.campaignId} className="bg-white rounded-lg shadow-md overflow-hidden">
                  {/* Campaign Header */}
                  <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{campaign.campaignTitle}</h3>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                          <span>Budget: <strong>₹{campaign.campaignBudget?.toLocaleString('en-IN')}</strong></span>
                          <span>Bidding Ends: <strong>{formatDate(campaign.biddingEndDate)}</strong></span>
                          <span>Deadline: <strong>{formatDate(campaign.deadline)}</strong></span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                          {campaign.totalBids} Bid{campaign.totalBids !== 1 ? 's' : ''}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {getDaysRemaining(campaign.biddingEndDate)} days left
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bids Preview */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      {campaign.bids.slice(0, 3).map((bid) => (
                        <div key={bid.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              {bid.creator?.profilePictureUrl && (
                                <img
                                  src={bid.creator.profilePictureUrl}
                                  alt={bid.creator.name}
                                  className="w-8 h-8 rounded-full"
                                />
                              )}
                              <span className="font-medium text-sm">{bid.creator?.name}</span>
                            </div>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getBidStatusColor(bid.status)}`}>
                              {bid.status}
                            </span>
                          </div>
                          <div className="text-lg font-bold text-green-600">₹{bid.reward?.toLocaleString('en-IN')}</div>
                          {bid.awarded && (
                            <div className="text-xs text-green-600 font-medium mt-1">✓ Awarded</div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* View All Bids Button */}
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-gray-600">
                        Showing {Math.min(3, campaign.bids.length)} of {campaign.totalBids} bids
                      </div>
                      <button
                        onClick={() => openBidsModal(campaign)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                      >
                        View All Bids
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* All Bids Modal */}
      {showBidsModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    All Bids for "{selectedCampaign.campaignTitle}"
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedCampaign.totalBids} total bids • Budget: ₹{selectedCampaign.campaignBudget?.toLocaleString('en-IN')}
                  </p>
                </div>
                <button
                  onClick={closeBidsModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 max-h-96 overflow-y-auto">
              <div className="space-y-4">
                {selectedCampaign.bids.map((bid) => (
                  <div key={bid.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {bid.creator?.profilePictureUrl && (
                          <img
                            src={bid.creator.profilePictureUrl}
                            alt={bid.creator.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <button
                            onClick={() => openCreatorProfile(bid.creator?.id)}
                            className="font-medium text-blue-600 hover:text-blue-800 underline text-left"
                          >
                            {bid.creator?.name || 'Unknown creator'}
                          </button>
                          <p className="text-sm text-gray-600">
                            {bid.creator?.followers ? `${bid.creator.followers.toLocaleString()} followers` : 'Creator Profile'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-green-600">₹{bid.reward?.toLocaleString('en-IN')}</div>
                        <div className={`px-2 py-1 text-xs font-semibold rounded-full ${getBidStatusColor(bid.status)} mt-1`}>
                          {bid.status}
                        </div>
                        {bid.awarded ? (
                          <div className="text-xs text-green-600 font-medium mt-1">✓ Awarded</div>
                        ) : (
                          <button
                            onClick={() => acceptBid(selectedCampaign.campaignId, bid.id)}
                            disabled={processingBidId === bid.id}
                            className={`mt-2 px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                              processingBidId === bid.id
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-green-600 hover:bg-green-700 text-white'
                            }`}
                          >
                            {processingBidId === bid.id ? 'Accepting...' : 'Accept Bid'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-end">
                <button
                  onClick={closeBidsModal}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Creator Profile Modal */}
      {showCreatorProfile && selectedCreatorId && (
        <PublicCreatorProfile
          creatorId={selectedCreatorId}
          onClose={closeCreatorProfile}
        />
      )}
    </>
  );
};

export default LiveBidsPage;

